import { REVIEW_GROUP_LABELS, type ReviewGroupCode } from "@hakili/domain-types";
import { formatDate, t } from "@hakili/i18n";
import { ActionBar, Button, Callout, GuardedButton, Modal, Textarea } from "@hakili/ui";
import { AlertTriangle, ArrowRight, Lock } from "lucide-react";
import { type ReactNode, useState } from "react";

import { AnalysisBanner, type AnalysisView, verdictLabel } from "./analyse.js";
import { fill } from "./dossiers.js";
import { DocumentViewer, type DocumentFileView } from "./document-viewer.js";
import { PointRow, TableHead } from "./revue-table.js";
import { ModalCancel } from "./modal-cancel.js";

/**
 * WF-BNK-05, the points in three steps (fifteen active since `OD-37` round 5, a file opened before keeping its sixteen), to the drawing
 * (`hakili-bank.jsx`, `ReviewFlow`) since round `L-0`: the three tabs with
 * their counts (Pièces 7, Cohérence 5, Conformité 4 , the door's own
 * groups), « N sur {total} points traités » (the door's own total), the step's title and hint, « Tout
 * confirmer » for the step, a card per point with « Conforme », « Signaler »
 * and its MANDATORY note, « Ouvrir la pièce et les vérifications » greyed
 * with `P1-BKW-006`'s round, the adaptive footer: « Refuser » greyed with its
 * round (`P1-LND-024`, `L-1`), « Demander les informations (N) » greyed with
 * its round (`L-3`) while a point is flagged, « Terminer la revue et préparer
 * l'offre » disabled while a point stands. Every verdict is the door's
 * (`PUT /bank/files/{id}/review/{pointId}`); the screen holds no state a door
 * did not answer.
 *
 * `A-1a` (D-1021-1, D3 §2): each point's state is the kit's `Verdict`, the footer is the kit's `ActionBar`, the
 * consignes are a `Callout`, the modals' free text is the kit's `Textarea`; « Tout confirmer » becomes
 * « Confirmer les propositions conformes » and confirms only what Hakili proposed conforme (R-6, D-1020-8): a
 * point with no proposal, or a proposal that is not conforme, is the analyste's own verdict, never a shortcut.
 */
export interface PointRevue {
  readonly id: string;
  readonly code: string;
  readonly groupCode: string;
  readonly label: string;
  readonly checked: boolean;
  readonly flagged: boolean;
  readonly flagNote: string | null;
  /** `YYYY-MM-DD` when the founder retired this point (`OD-37` round 5) and the file was opened before: the row says so. */
  readonly retiredAt: string | null;
}

/** `OD-37` round 10b (§8.2): P14 , « Criblage LCB-FT et PEP » , is the review point `lcbFtPep`. */
export const SCREENING_POINT_CODE = "lcbFtPep";

export interface ProgressionRevue {
  readonly handled: number;
  readonly total: number;
  readonly outstanding: number;
  readonly flagged: number;
  readonly byGroup: Readonly<Record<string, { handled: number; total: number }>>;
}

const asString = (value: unknown): string | null => (typeof value === "string" ? value : null);

export function pointFrom(row: unknown): PointRevue | null {
  if (typeof row !== "object" || row === null) return null;
  const raw = row as Record<string, unknown>;
  const id = asString(raw["id"]);
  const code = asString(raw["code"]);
  const groupCode = asString(raw["groupCode"]);
  const label = asString(raw["label"]);
  if (id === null || code === null || groupCode === null || label === null) return null;
  return {
    id,
    code,
    groupCode,
    label,
    checked: raw["checked"] === true,
    flagged: raw["flagged"] === true,
    flagNote: asString(raw["flagNote"]),
    retiredAt: asString(raw["retiredAt"]),
  };
}

export function pointsFrom(answer: unknown): PointRevue[] {
  if (typeof answer !== "object" || answer === null) return [];
  const points = (answer as Record<string, unknown>)["points"];
  if (!Array.isArray(points)) return [];
  return points.map(pointFrom).filter((point): point is PointRevue => point !== null);
}

/** The progress as the door answers it , on the take, on a verdict, or on the file's own read under `progress`. */
export function progressionFrom(answer: unknown): ProgressionRevue | null {
  if (typeof answer !== "object" || answer === null) return null;
  const raw = answer as Record<string, unknown>;
  const source = (
    typeof raw["progress"] === "object" && raw["progress"] !== null ? raw["progress"] : raw
  ) as Partial<ProgressionRevue>;
  if (typeof source.total !== "number") return null;
  return {
    handled: source.handled ?? 0,
    total: source.total,
    outstanding: source.outstanding ?? 0,
    flagged: source.flagged ?? 0,
    byGroup: source.byGroup ?? {},
  };
}

const STEPS: readonly { readonly group: ReviewGroupCode }[] = [
  { group: "pieces" },
  { group: "coherence" },
  { group: "conformite" },
];

/**
 * `L-1b`, `M4` (c) (`P1-BKW-011`, WF-BNK-10 step 4): the chef's instructions on
 * a returned offer, the amber block at the TOP of the review , above the
 * points and above the composer alike, since a returned file whose sixteen
 * points stand opens on the composer , each row cleared by « Marquer comme
 * traitée », a door, and nothing else.
 */
export function Instructions(props: {
  readonly instructions: readonly { readonly id: string; readonly text: string }[];
  readonly busy: boolean;
  readonly onTreated: (instructionId: string) => void;
}): React.JSX.Element | null {
  const { instructions } = props;
  if (instructions.length === 0) return null;
  return (
    <Callout
      tone="warn"
      testId="bank-revue-consignes"
      icon={<AlertTriangle className="h-4 w-4" aria-hidden="true" />}
      className="mb-4"
    >
      <p className="text-sm font-semibold">{t("bank.revue.consignesTitre")}</p>
      <p className="mb-3 mt-1 text-sm">{t("bank.revue.consignesAide")}</p>
      <ul className="space-y-2">
        {instructions.map((one) => (
          <li
            key={one.id}
            className="flex items-start justify-between gap-3 rounded-xl border border-amber-200 bg-white px-3.5 py-2.5"
            data-testid={`bank-revue-consigne-${one.id}`}
          >
            <p className="text-sm text-gray-900">{one.text}</p>
            <button
              type="button"
              disabled={props.busy}
              onClick={() => {
                props.onTreated(one.id);
              }}
              className="shrink-0 rounded-lg border border-amber-200 bg-white px-2.5 py-1.5 text-sm font-medium text-amber-700 hover:bg-amber-50 disabled:opacity-60"
              data-testid={`bank-revue-consigne-traitee-${one.id}`}
            >
              {t("bank.revue.consigneTraitee")}
            </button>
          </li>
        ))}
      </ul>
    </Callout>
  );
}

/**
 * WF-BNK-07 step 3: the file at `retour` , the banner and the request as sent. The drawing's « Informations
 * reçues » simulation control is not built , the file comes back by the resubmission's event , and, with no
 * door behind it, it is no longer drawn (`A-1a`, D-1019-2).
 */
export function RetourPanel(props: {
  readonly completion: {
    readonly requestedAt: string;
    readonly message: string;
    readonly clientMustAct: boolean;
    readonly items: readonly { readonly label: string; readonly documentCode: string | null }[];
  } | null;
}): ReactNode {
  const completion = props.completion;
  return (
    <section className="rounded-2xl border border-gray-200 bg-white px-6 py-5" data-testid="bank-retour">
      <Callout tone="info" testId="bank-retour-banniere">
        {completion === null || completion.clientMustAct
          ? t("bank.retour.banniereClient")
          : t("bank.retour.banniereInterne")}
      </Callout>
      {completion === null ? null : (
        <div className="mt-4" data-testid="bank-retour-demande">
          <p className="text-sm text-gray-600">
            {fill("bank.retour.demandeLe", { date: formatDate(new Date(completion.requestedAt)) })}
          </p>
          <ul className="mt-2 space-y-1">
            {completion.items.map((item, index) => (
              <li key={`${String(index)}-${item.label}`} className="text-sm text-gray-700">
                {item.label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

export interface CompletionRequestInput {
  readonly message: string;
  readonly target: "client" | "interne";
  readonly pieces: readonly string[];
}

/** WF-BNK-07 step 2's prefill: one line per flagged point, « - <libellé> : <note> ». */
export const prefillOf = (points: readonly PointRevue[]): string =>
  points
    .filter((point) => point.flagged)
    .map((point) => `- ${point.label} : ${point.flagNote ?? ""}`)
    .join("\n");

/**
 * The drawing's `InfoRequestModal`: the sentence, the prefill's note in two keys
 * (never parenthesised), the text freely editable and erasable, rebuilt from the
 * prefill each time it opens (the caller remounts it by key), « Envoyer la demande
 * à Hakili » disabled while the text is empty. The target and the standard pièce
 * are the record's `clientMustAct` and `piece_definition_code`, controls by a
 * drawing in words, flagged in the addendum.
 */
/**
 * Round `L-3b`, `M1` (`P1-BKW-008`, WF-BNK-08; `hakili-bank.jsx` `RefuseModal`):
 * « Refuser le dossier », the sentence, « Motif du refus » over the closed list
 * the door serves (05 §2.6.5's four, in the drawing's order), « Annuler »,
 * « Confirmer le refus » in the rose family. The select opens on « Choisir un
 * motif » and the confirm stays disabled until one is chosen (M0 (b): a motive
 * nobody chose would teach the calibration a threshold nobody meant , a drawing
 * in words, F8 for the designer). Opened by the analyst's « Refuser » in the
 * review footer and by the chef's on his decision screen , the same modal, the
 * same list, one fact by the door.
 */
export function RefuseModal(props: {
  readonly open: boolean;
  readonly reasons: readonly { readonly code: string; readonly label: string }[];
  readonly busy: boolean;
  readonly onClose: () => void;
  readonly onConfirm: (reasonCode: string) => void;
}): ReactNode {
  const [reason, setReason] = useState<string>("");
  if (!props.open) return null;
  return (
    <Modal open title={t("bank.refus.titre")} onClose={props.onClose} cross={false} size="lg">
      <div data-testid="bank-refus">
        <p className="text-sm text-gray-600">{t("bank.refus.phrase")}</p>
        <label className="mt-4 block text-sm font-medium text-gray-700">
          {t("bank.refus.motif")}
          <select
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
            }}
            className="mt-1.5 w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-indigo-400"
            data-testid="bank-refus-motif"
          >
            <option value="">{t("bank.refus.choisir")}</option>
            {props.reasons.map((one) => (
              <option key={one.code} value={one.code}>
                {one.label}
              </option>
            ))}
          </select>
        </label>
        {/* D2-design §12, call 3: « Annuler » first, the action last and dark. */}
        <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
          <ModalCancel testId="bank-refus-annuler" onClick={props.onClose} />
          {reason === "" ? (
            <GuardedButton
              disabled
              reason={t("bank.refus.choisirMotif")}
              reasonDress="chip"
              reasonIcon={<Lock className="h-[15px] w-[15px] shrink-0" aria-hidden="true" />}
              testId="bank-refus-confirmer"
            >
              {t("bank.refus.confirmer")}
            </GuardedButton>
          ) : (
            <Button
              variant="ink"
              disabled={props.busy}
              onClick={() => {
                props.onConfirm(reason);
              }}
              data-testid="bank-refus-confirmer"
            >
              {t("bank.refus.confirmer")}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

/**
 * The day a point was retired, as the shared formatter writes it (« 1er octobre 2026 »), its words bound by no-break spaces so the
 * date never splits across two lines. The door's `YYYY-MM-DD`; anything else is printed as the door gave it.
 */
function retiredOn(day: string): string {
  const instant = new Date(`${day}T12:00:00Z`);
  return Number.isNaN(instant.getTime()) ? day : formatDate(instant).replace(/ /gu, "\u00A0");
}

/** « Terminer la revue et préparer l'offre » and its arrow: one line at every width, live and inert (designer, `fb687083`). */
function TerminerLabel(): ReactNode {
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap">
      {t("bank.revue.terminer")}
      <ArrowRight className="h-4 w-4" aria-hidden="true" />
    </span>
  );
}

/**
 * `REFUSAL-BY` (frame 51's meta line): who refused, as the door tells it, typed. `analyste` and `chef` carry a name; `comite` carries none (the screen
 * prints D2's « Comité de crédit » from the kind); `null` is a refusal the bank did not make in person, or one stored with no actor.
 */
export type RefusalBy =
  | { readonly kind: "analyste" | "chef"; readonly name: string }
  | { readonly kind: "comite"; readonly name: null };
export interface RefusalRead {
  readonly code: string;
  readonly label: string;
  readonly at: string;
  readonly by: RefusalBy | null;
}

/** `L-3b`, `M1`: the file's own refusal (the banner reads the row), `null` while none; `by` parsed, never cast (A48). */
export function refusalFrom(answer: unknown): RefusalRead | null {
  const raw = (answer as { refusal?: unknown } | null)?.refusal;
  if (raw === null || typeof raw !== "object") return null;
  const row = raw as { code?: unknown; label?: unknown; at?: unknown; by?: unknown };
  if (typeof row.code !== "string" || typeof row.label !== "string") return null;
  const by = row.by as { kind?: unknown; name?: unknown } | null | undefined;
  const refuser: RefusalBy | null =
    by === null || by === undefined || typeof by !== "object"
      ? null
      : by.kind === "comite" && by.name === null
        ? { kind: "comite", name: null }
        : (by.kind === "analyste" || by.kind === "chef") && typeof by.name === "string"
          ? { kind: by.kind, name: by.name }
          : null;
  return { code: row.code, label: row.label, at: typeof row.at === "string" ? row.at : "", by: refuser };
}

/**
 * `L-3b`, `M1`: the workspace at `refuse` , ONE full-width callout reading the ROW (R-A's rule: the fact is the file's, never
 * the session's), one paragraph. « Rouvrir » is not drawn: D3 lists it as a control with no door, and D-1019-2 says such a
 * control is not drawn , not inert, not greyed, no reason (designer, `e74ce6f9`).
 */
export function InfoRequestModal(props: {
  readonly open: boolean;
  readonly flaggedCount: number;
  readonly prefill: string;
  readonly busy: boolean;
  readonly onClose: () => void;
  readonly onSend: (request: CompletionRequestInput) => void;
}): ReactNode {
  const [text, setText] = useState<string>(props.prefill);
  const [target, setTarget] = useState<"client" | "interne">("client");
  if (!props.open) return null;
  const sendable = text.trim() !== "" && !props.busy;
  return (
    <Modal open title={t("bank.demande.titre")} onClose={props.onClose} cross={false} size="lg">
      <div data-testid="bank-demande">
        <p className="text-sm text-gray-600">{t("bank.demande.phrase")}</p>
        <label htmlFor="bank-demande-texte" className="mt-3.5 block text-sm font-semibold text-gray-700">
          {t("bank.demande.votreDemande")}
        </label>
        <Textarea
          id="bank-demande-texte"
          value={text}
          onChange={setText}
          placeholder={t("bank.demande.aide")}
          rows={5}
          className="mt-2 resize-none"
          testId="bank-demande-texte"
        />
        <p className="mt-1.5 text-sm text-[color:var(--hk-text-2)]" data-testid="bank-demande-aide">
          {t("bank.demande.aideSousLaBoite")}
        </p>
        <div className="mt-3">
          <span id="bank-demande-cible-libelle" className="block text-sm font-semibold text-gray-700">
            {t("bank.demande.quiTraite")}
          </span>
          <div
            role="group"
            aria-labelledby="bank-demande-cible-libelle"
            className="mt-1.5 inline-flex overflow-hidden rounded-xl border border-[color:var(--hk-line)]"
          >
            {(
              [
                ["client", "bank.demande.parClient"],
                ["interne", "bank.demande.parHakili"],
              ] as const
            ).map(([value, key]) => (
              <button
                key={value}
                type="button"
                aria-pressed={target === value}
                onClick={() => {
                  setTarget(value);
                }}
                className={`px-4 py-2 text-sm font-semibold ${
                  target === value
                    ? "bg-[color:var(--hk-brand)] text-white"
                    : "bg-white text-[color:var(--hk-text)] hover:bg-gray-50"
                }`}
                data-testid={`bank-demande-cible-${value}`}
              >
                {t(key)}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
          <ModalCancel testId="bank-demande-annuler" onClick={props.onClose} />
          <button
            type="button"
            disabled={!sendable}
            onClick={() => {
              props.onSend({ message: text.trim(), target, pieces: [] });
            }}
            className="rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
            data-testid="bank-demande-envoyer"
          >
            {t("bank.demande.envoyer")}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * The register's three classes, in the record's words (underwriter-workflow §9.2): D2 names only « Constat non bloquant » (`app.js:162`),
 * and « à lever » is what the analyst must clear before deciding, so it is never folded into « signalé ».
 */
const FINDING_WORDS = {
  bloquant: { kind: "non_conforme", word: "bank.visionneuse.constatBloquant" },
  a_lever: { kind: "incertain", word: "bank.visionneuse.constatALever" },
  signale: { kind: "signale", word: "bank.visionneuse.constatSignale" },
} as const;

export function Revue(props: {
  /** `ARC2-SHEET`: goes to « Politique » (the sheet's null card, for the grid cause). */
  readonly onPolitique?: () => void;
  readonly points: readonly PointRevue[];
  readonly progression: ProgressionRevue | null;
  readonly busy: boolean;
  readonly onPoint: (
    pointId: string,
    verdict: {
      checked: boolean;
      flagged: boolean;
      flagNote?: string;
      /** `OD-37` round 10b (§8.2): « criblage effectué » and « outil ou source », sent on P14 only. */
      screening?: { done: boolean; source?: string };
    },
  ) => void;
  readonly onTerminer: () => void;
  /**
   * `ARC-1`: the door that opens a point's pièce in the viewer (`GET /bank/files/{id}/pieces/{code}/file`), by the pièce's code; it
   * answers the files in upload order, or `null` for the 404. Absent, the evidence is a plain place and the viewer is not drawn
   * (D-1019-2: a control without a door is not drawn).
   */
  readonly documentDoor?: ((documentCode: string) => Promise<readonly DocumentFileView[] | null>) | undefined;
  /**
   * `L-1b`, `M4` (c) (`P1-BKW-011`, WF-BNK-10 step 4): the chef's instructions
   * on a returned offer, the amber block at the top of the review, each row
   * cleared by « Marquer comme traitées » , a door, and nothing else.
   */
  readonly instructions?: readonly { readonly id: string; readonly text: string }[];
  readonly onInstructionTreated?: (instructionId: string) => void;
  /**
   * Round `L-3`, `M1` (`P1-BKW-007`, WF-BNK-07): « Demander les informations (N) »
   * opens the drawing's modal; the request goes by the analyst's own door. The
   * standard pièces the modal may name are the door's list, never a copy here.
   */
  readonly onAsk?: (request: CompletionRequestInput) => void;
  /**
   * Round `L-3b`, `M1` (`P1-BKW-008`, WF-BNK-05 step 8, WF-BNK-08): « Refuser »,
   * always available beside the primary that changes with the flags, opens the
   * drawing's modal over the door's closed list; the refusal goes by the door.
   */
  readonly onRefuse?: (reasonCode: string) => void;
  readonly refusalReasons?: readonly { readonly code: string; readonly label: string }[];
  /**
   * `U-2`, `M3` (WF-BNK-05, OD-37 on the bank's side): Hakili's analysis , a proposal with its
   * evidence on every point, « Confirmer » adopting it by her own door, « Conforme » / « Signaler »
   * her correction; `null` with no run (R81's sentence), the failure's sentence when it failed.
   */
  readonly analysis?: AnalysisView | null;
  readonly analysisSentence?: string | null;
}): React.JSX.Element {
  const [step, setStep] = useState(0);
  const [openId, setOpenId] = useState<string | null>(null);
  // A counter keys the viewer: each opening mounts it afresh and so asks the door again (no link is kept).
  const [viewing, setViewing] = useState<{ readonly id: string; readonly opening: number } | null>(null);
  const [noteFor, setNoteFor] = useState<string | null>(null);
  // A counter and not a boolean: each opening remounts the modal on the prefill (WF-BNK-07 step 2).
  const [askOpen, setAskOpen] = useState<number>(0);
  const [refuseOpen, setRefuseOpen] = useState<number>(0);
  const [noteText, setNoteText] = useState("");
  // `OD-37` round 10b (§8.2, D-6): the screening field of her act on P14, by point.
  const [screenings, setScreenings] = useState<ReadonlyMap<string, { done: boolean | null; source: string }>>(
    new Map(),
  );
  const progression = props.progression;
  const screeningFor = (
    point: PointRevue,
  ): { guard: string | null; screening: { done: boolean; source?: string } | undefined } => {
    if (point.code !== SCREENING_POINT_CODE) return { guard: null, screening: undefined };
    const given = screenings.get(point.id);
    const source = given?.source.trim() ?? "";
    const done = given?.done ?? null;
    if (done === null || (done && source === "")) {
      return { guard: t("bank.revue.criblageMotif"), screening: undefined };
    }
    return { guard: null, screening: done ? { done: true, source } : { done: false } };
  };
  const settle = (
    point: PointRevue,
    verdict: { checked: boolean; flagged: boolean; flagNote?: string },
  ): void => {
    const { screening } = screeningFor(point);
    props.onPoint(point.id, screening === undefined ? verdict : { ...verdict, screening });
  };
  const handledOf = (group: string): { handled: number; total: number } =>
    progression?.byGroup[group] ?? {
      handled: props.points.filter((point) => point.groupCode === group && (point.checked || point.flagged))
        .length,
      total: props.points.filter((point) => point.groupCode === group).length,
    };
  const total = progression?.total ?? props.points.length;
  const handled =
    progression?.handled ?? props.points.filter((point) => point.checked || point.flagged).length;
  const flagged = progression?.flagged ?? props.points.filter((point) => point.flagged).length;
  const outstanding = total - handled;
  const current = STEPS[step] ?? STEPS[0];
  const items =
    current === undefined ? [] : props.points.filter((point) => point.groupCode === current.group);

  const proposalOf = (code: string) => props.analysis?.points.find((one) => one.code === code) ?? null;
  // R-6 (D-1020-8): the points Hakili PROPOSED conforme and that stand open. A point with no proposal, a
  // proposal that is not conforme, or P14 while its screening is not given, is the analyste's own verdict.
  const confirmable = items.filter(
    (point) =>
      !point.checked &&
      !point.flagged &&
      screeningFor(point).guard === null &&
      proposalOf(point.code)?.verdict === "conforme",
  );
  const confirmStep = (): void => {
    for (const point of confirmable) settle(point, { checked: true, flagged: false });
  };
  const saveNote = (): void => {
    if (noteFor === null || noteText.trim() === "") return;
    const point = props.points.find((one) => one.id === noteFor);
    const verdict = { checked: false, flagged: true, flagNote: noteText.trim() };
    if (point === undefined) props.onPoint(noteFor, verdict);
    else settle(point, verdict);
    setNoteFor(null);
    setNoteText("");
  };

  /**
   * P14's form (`OD-37` round 10b, the founder's keep), drawn to D2 frames 133 and 134: « Criblage effectué » as a two-button group
   * (« Oui » / « Non », the chosen one filled) beside « Outil ou source du criblage ». The reason its acts wait on is said once, in the
   * lock chip under the buttons (`PointRow`), never here.
   */
  const screeningForm = (point: PointRevue): ReactNode =>
    point.code !== SCREENING_POINT_CODE ? null : (
      <div
        className="my-3.5 grid max-w-[620px] grid-cols-2 gap-5 max-[1099px]:grid-cols-1"
        data-testid={`point-${point.code}-criblage`}
      >
        <div>
          <span
            className="mb-1.5 block text-sm font-semibold text-[color:var(--hk-text)]"
            id={`point-${point.code}-criblage-titre`}
          >
            {t("bank.revue.criblageTitre")}
          </span>
          <div
            role="group"
            aria-labelledby={`point-${point.code}-criblage-titre`}
            className="inline-flex overflow-hidden rounded-[10px] border border-[color:var(--hk-line-strong)]"
          >
            {([true, false] as const).map((value) => (
              <button
                key={String(value)}
                type="button"
                aria-pressed={screenings.get(point.id)?.done === value}
                onClick={() => {
                  setScreenings((now) =>
                    new Map(now).set(point.id, { source: "", ...now.get(point.id), done: value }),
                  );
                }}
                className={
                  "min-h-[40px] min-w-[60px] border-r border-[color:var(--hk-line-strong)] px-4 text-sm font-semibold last:border-r-0 " +
                  (screenings.get(point.id)?.done === value
                    ? "bg-[color:var(--hk-brand)] text-white"
                    : "bg-white text-[color:var(--hk-text)] hover:bg-[color:var(--hk-surface-2)]")
                }
                data-testid={`point-${point.code}-criblage-${value ? "oui" : "non"}`}
              >
                {t(value ? "bank.revue.criblageOui" : "bank.revue.criblageNon")}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label
            className="mb-1.5 block text-sm font-semibold text-[color:var(--hk-text)]"
            htmlFor={`point-${point.code}-criblage-source`}
          >
            {t("bank.revue.criblageSource")}
          </label>
          <input
            id={`point-${point.code}-criblage-source`}
            value={screenings.get(point.id)?.source ?? ""}
            placeholder={t("bank.revue.criblagePlaceholder")}
            onChange={(event) => {
              setScreenings((now) =>
                new Map(now).set(point.id, {
                  done: now.get(point.id)?.done ?? null,
                  source: event.target.value,
                }),
              );
            }}
            className="min-h-[44px] w-full rounded-[10px] border border-[color:var(--hk-line-strong)] bg-white px-3 text-sm text-[color:var(--hk-text)] placeholder:text-[color:var(--hk-text-2)]"
            data-testid={`point-${point.code}-criblage-source`}
          />
        </div>
      </div>
    );

  return (
    <div data-testid="bank-revue-flux">
      {props.analysis === undefined ? null : (
        <AnalysisBanner
          analysis={props.analysis}
          sentence={props.analysisSentence ?? null}
          pointCount={total}
        />
      )}
      <Instructions
        instructions={props.instructions ?? []}
        busy={props.busy}
        onTreated={(id) => {
          props.onInstructionTreated?.(id);
        }}
      />
      <section
        className="mb-4 overflow-hidden rounded-2xl border border-[color:var(--hk-line)] bg-white"
        data-testid="bank-revue-table"
      >
        <div
          role="tablist"
          aria-label={t("bank.revue.etapes")}
          className="grid grid-cols-3 border-b border-[color:var(--hk-line)]"
        >
          {STEPS.map((one, i) => {
            const counts = handledOf(one.group);
            const ratio = counts.total === 0 ? 0 : (counts.handled / counts.total) * 100;
            return (
              <button
                key={one.group}
                type="button"
                role="tab"
                aria-selected={i === step}
                onClick={() => {
                  setStep(i);
                  setOpenId(null);
                }}
                className={
                  "relative flex items-center justify-between border-r border-[color:var(--hk-line)] px-5 pb-4 pt-3.5 text-[15px] font-semibold last:border-r-0 hover:bg-[color:var(--hk-surface-2)] focus-visible:outline focus-visible:outline-[3px] focus-visible:-outline-offset-[3px] focus-visible:outline-[color:var(--hk-focus)] " +
                  (i === step
                    ? "bg-[color:var(--hk-surface-2)] text-[color:var(--hk-text)]"
                    : "text-[color:var(--hk-text-2)]")
                }
                data-testid={`bank-revue-groupe-${one.group}`}
              >
                {REVIEW_GROUP_LABELS[one.group]}
                <code className="font-medium text-[color:var(--hk-text-2)]">
                  {counts.handled}/{counts.total}
                </code>
                <i
                  aria-hidden="true"
                  className="absolute bottom-0 left-0 block h-[3px] w-full bg-[color:var(--hk-line-soft)]"
                >
                  <span
                    className={
                      "absolute inset-y-0 left-0 " +
                      (i === step ? "bg-[color:var(--hk-brand)]" : "bg-[color:var(--hk-ok)]")
                    }
                    style={{ width: `${String(ratio)}%` }}
                  />
                </i>
              </button>
            );
          })}
        </div>
        <TableHead />
        <ol aria-label={t("bank.revue.listePoints")}>
          {items.map((point) => {
            const proposal = proposalOf(point.code);
            // `DX-B-016`, `DX-B-017`: the LCB-FT card stacks its question above its controls, and the reason its controls
            // wait on is said ONCE, at the question (`InertAct` points at it).
            const guard = screeningFor(point).guard;
            const reasonId = `point-${point.code}-criblage-motif`;
            return (
              <PointRow
                {...(props.onPolitique === undefined ? {} : { onPolitique: props.onPolitique })}
                key={point.id}
                number={props.points.indexOf(point) + 1}
                code={point.code}
                id={point.id}
                label={point.label}
                checked={point.checked}
                flagged={point.flagged}
                flagNote={point.flagNote}
                retiredNote={
                  point.retiredAt === null
                    ? null
                    : fill("bank.revue.pointRetire", { date: retiredOn(point.retiredAt) })
                }
                proposal={proposal}
                open={openId === point.id}
                busy={props.busy}
                guard={guard}
                guardId={point.code === SCREENING_POINT_CODE && guard !== null ? reasonId : null}
                screening={screeningForm(point)}
                canView={props.documentDoor !== undefined}
                noteOpen={noteFor === point.id}
                noteText={noteText}
                onToggle={() => {
                  setOpenId(openId === point.id ? null : point.id);
                  setNoteFor(null);
                }}
                onView={() => {
                  setViewing((now) => ({ id: point.id, opening: (now?.opening ?? 0) + 1 }));
                }}
                onConfirm={() => {
                  settle(point, { checked: true, flagged: false });
                  setOpenId(null);
                }}
                onConform={() => {
                  settle(point, { checked: true, flagged: false });
                  setOpenId(null);
                }}
                onFlagOpen={() => {
                  setNoteFor(point.id);
                  setNoteText(point.flagNote ?? "");
                }}
                onReopen={() => {
                  settle(point, { checked: false, flagged: false });
                }}
                onNoteText={setNoteText}
                onNoteSave={saveNote}
              />
            );
          })}
        </ol>
        {confirmable.length === 0 ? null : (
          <div className="flex justify-end border-t border-[color:var(--hk-line-soft)] bg-[color:var(--hk-surface-2)] px-5 py-3">
            <button
              type="button"
              disabled={props.busy}
              onClick={confirmStep}
              className="inline-flex min-h-[34px] items-center rounded-[10px] px-3 text-sm font-semibold text-[color:var(--hk-brand-strong)] hover:bg-[color:var(--hk-brand-wash)] disabled:opacity-60"
              data-testid="bank-revue-tout-confirmer"
            >
              {`${t("bank.revue.confirmerConformes")} · ${current === undefined ? "" : REVIEW_GROUP_LABELS[current.group]}`}
            </button>
          </div>
        )}
      </section>
      <ActionBar
        label={t("bank.revue.barreActions")}
        hint={
          flagged === 0 && outstanding > 0 ? undefined : (
            <span data-testid="bank-revue-pied">
              {/* `L-3b`, `M1` (`L-3`'s obligation): the sentence follows the PRIMARY , the moment a
              point is flagged the control asks, so the sentence says the flags; the outstanding
              count speaks only while nothing is flagged; all conforme, the third (WF-BNK-05 step 7). */}
              {flagged > 0
                ? fill(flagged === 1 ? "bank.revue.piedSignale" : "bank.revue.piedSignalePluriel", {
                    n: String(flagged),
                  })
                : t("bank.revue.piedConforme")}
            </span>
          )
        }
      >
        <span className="inline-flex items-center gap-2.5 text-[15px] text-[color:var(--hk-text)]">
          <b
            className="font-[family-name:var(--hk-mono,ui-monospace)] text-base font-semibold"
            data-testid="bank-revue-compteur"
          >{`${String(handled)}/${String(total)}`}</b>
          {t("bank.revue.compteur")}
          <span
            role="progressbar"
            aria-valuenow={handled}
            aria-valuemin={0}
            aria-valuemax={total}
            aria-label={t("bank.revue.compteurProgres")}
            className="block h-1.5 w-[130px] overflow-hidden rounded bg-[color:var(--hk-line)]"
            data-testid="bank-revue-progres"
          >
            <i
              className="block h-full bg-[color:var(--hk-ok)]"
              style={{ width: `${String(total === 0 ? 0 : (handled / total) * 100)}%` }}
            />
          </span>
        </span>
        <span aria-hidden="true" className="min-w-0 flex-1" />
        {props.onRefuse === undefined ? null : (
          <button
            type="button"
            disabled={props.busy}
            onClick={() => {
              setRefuseOpen((count) => count + 1);
            }}
            className="rounded-xl px-3.5 py-2.5 text-sm font-semibold text-[color:var(--hk-bad)] hover:bg-[color:var(--hk-bad-bg)] disabled:opacity-40"
            data-testid="bank-revue-refuser"
          >
            {t("bank.revue.refuser")}
          </button>
        )}
        {flagged > 0 ? (
          // WF-BNK-05's adaptive footer, WF-BNK-07's trigger: the primary control becomes the
          // request once a point is flagged; the modal opens on the prefill and goes by the door.
          <button
            type="button"
            disabled={props.busy || props.onAsk === undefined}
            onClick={() => {
              setAskOpen((count) => count + 1);
            }}
            className="rounded-xl bg-indigo-600 px-3.5 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
            data-testid="bank-revue-primaire"
          >
            {`${t("bank.revue.demander")} (${String(flagged)})`}
          </button>
        ) : outstanding > 0 ? (
          <GuardedButton
            disabled
            reason={t("bank.revue.piedEnCours")}
            variant="primary"
            reasonDress="chip"
            reasonIcon={<Lock className="h-[15px] w-[15px] shrink-0" aria-hidden="true" />}
            testId="bank-revue-primaire"
          >
            <TerminerLabel />
          </GuardedButton>
        ) : (
          <GuardedButton
            onClick={() => {
              if (!props.busy) props.onTerminer();
            }}
            variant="primary"
            testId="bank-revue-primaire"
          >
            <TerminerLabel />
          </GuardedButton>
        )}
      </ActionBar>
      {refuseOpen > 0 && props.onRefuse !== undefined ? (
        <RefuseModal
          key={refuseOpen}
          open
          reasons={props.refusalReasons ?? []}
          busy={props.busy}
          onClose={() => {
            setRefuseOpen(0);
          }}
          onConfirm={(reasonCode) => {
            setRefuseOpen(0);
            props.onRefuse?.(reasonCode);
          }}
        />
      ) : null}
      {askOpen > 0 && props.onAsk !== undefined ? (
        <InfoRequestModal
          key={askOpen}
          open
          flaggedCount={flagged}
          prefill={prefillOf(props.points)}
          busy={props.busy}
          onClose={() => {
            setAskOpen(0);
          }}
          onSend={(request) => {
            setAskOpen(0);
            props.onAsk?.(request);
          }}
        />
      ) : null}
      {viewing === null
        ? null
        : (() => {
            const point = props.points.find((one) => one.id === viewing.id);
            const proposal = point === undefined ? null : proposalOf(point.code);
            const door = props.documentDoor;
            if (
              point === undefined ||
              proposal === null ||
              proposal.evidence?.kind !== "document" ||
              door === undefined
            ) {
              return null;
            }
            const code = proposal.evidence.documentCode;
            return (
              <DocumentViewer
                key={viewing.opening}
                title={point.label}
                source={
                  proposal.evidence.receivedAt === null
                    ? fill("bank.visionneuse.source", { n: String(proposal.evidence.page) })
                    : `${fill("bank.visionneuse.source", { n: String(proposal.evidence.page) })} · ${fill("bank.visionneuse.depose", { date: formatDate(new Date(proposal.evidence.receivedAt)) })}`
                }
                door={() => door(code)}
                checks={[
                  ...(props.analysis?.points ?? [])
                    .filter((read) => read.documentCodes.includes(code))
                    .map((read) => ({
                      kind: read.verdict,
                      label: verdictLabel(read.verdict),
                      reason: read.reason,
                    })),
                  ...(props.analysis?.registre ?? [])
                    .filter((finding) => finding.documentCode === code)
                    .map((finding) => ({
                      kind: FINDING_WORDS[finding.severity].kind,
                      label: t(FINDING_WORDS[finding.severity].word),
                      reason: finding.reason,
                    })),
                ]}
                verdict={{ kind: proposal.verdict, label: verdictLabel(proposal.verdict) }}
                reason={proposal.reason}
                acts={
                  point.checked || point.flagged
                    ? null
                    : {
                        onConform: () => {
                          settle(point, { checked: true, flagged: false });
                          setViewing(null);
                          setOpenId(null);
                        },
                        onFlag: () => {
                          setViewing(null);
                          setOpenId(point.id);
                          setNoteFor(point.id);
                          setNoteText("");
                        },
                      }
                }
                onClose={() => {
                  setViewing(null);
                }}
              />
            );
          })()}
    </div>
  );
}
